import flip from "ramda/es/flip";
import prop from "ramda/es/prop";
import propEq from "ramda/es/propEq";
import propOr from "ramda/es/propOr";
import defaultToEmptyObject from "ramda-extension/es/defaultToEmptyObject";
import lastArg from "ramda-extension/es/lastArg";
import { createSelector } from 'reselect';
import { REDUCER_NAME, requestStates } from '../constants';
/**
 * Get object containing both an array of current requestUuids and object with all statuses
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestStatusState(state)
 *
 */ export const getRequestStatusState = createSelector(prop(REDUCER_NAME), defaultToEmptyObject);
/**
 * Get an array containing all of currently watched requestUuids.
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestUuids(state)
 *
 */ export const getRequestUuids = createSelector(getRequestStatusState, propOr([], 'requestUuids'));
/**
 * Get an object containing all of currently watched statuses
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestStatuses(state)
 *
 */ export const getRequestStatuses = createSelector(getRequestStatusState, propOr({}, 'statuses'));
/**
 * Get current status of request using its requestUuid
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getRequestStatusById(state)(requestUuid)
 *
 */ export const getRequestStatusById = createSelector(getRequestStatuses, (statuses)=>(requestUuid)=>prop(requestUuid, statuses));
const getIsEqualToStatus = (requestStatus)=>flip(propEq(requestStatus));
/**
 * Get whether current status with passed requestUuid is in requestStates.COMPLETED state
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getIsRequestCompleted(state, requestUuid)
 *
 */ export const getIsRequestCompleted = createSelector(getRequestStatuses, lastArg, getIsEqualToStatus(requestStates.COMPLETED));
/**
 * Get whether current status with passed requestUuid is in requestStates.IN_QUEUE state
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getIsRequestInQueue(state, requestUuid)
 *
 */ export const getIsRequestInQueue = createSelector(getRequestStatuses, lastArg, getIsEqualToStatus(requestStates.IN_QUEUE));
/**
 * Get whether current status with passed requestUuid is in requestStates.REQUESTED state
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getIsRequestRequested(state, requestUuid)
 *
 */ export const getIsRequestRequested = createSelector(getRequestStatuses, lastArg, getIsEqualToStatus(requestStates.REQUESTED));
/**
 * Get whether current status with passed requestUuid is in requestStates.CANCELED state
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getIsRequestCanceled(state, requestUuid)
 *
 */ export const getIsRequestCanceled = createSelector(getRequestStatuses, lastArg, getIsEqualToStatus(requestStates.CANCELED));
/**
 * Get whether current status with passed requestUuid is in requestStates.IN_PROGRESS state
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getIsRequestInProgress(state, requestUuid)
 *
 */ export const getIsRequestInProgress = createSelector(getRequestStatuses, lastArg, getIsEqualToStatus(requestStates.IN_PROGRESS));
/**
 * Get whether current status with passed requestUuid is in requestStates.ERROR state
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getHasRequestError(state, requestUuid)
 *
 */ export const getHasRequestError = createSelector(getRequestStatuses, lastArg, getIsEqualToStatus(requestStates.ERROR));
/**
 * Get whether current status with passed requestUuid is in requestStates.PAUSED state
 *
 * @documented
 * @category Selector
 * @example
 *
 * 	getIsRequestPaused(state, requestUuid)
 *
 */ export const getIsRequestPaused = createSelector(getRequestStatuses, lastArg, getIsEqualToStatus(requestStates.PAUSED));
export const makeGetRequestState = ()=>createSelector((state, requestUuid)=>getRequestStatuses(state)[requestUuid], (status)=>{
        return {
            isLoading: [
                requestStates.REQUESTED,
                requestStates.IN_QUEUE,
                requestStates.IN_PROGRESS,
                requestStates.PAUSED
            ].includes(status),
            isError: status === requestStates.ERROR,
            isSuccess: status === requestStates.COMPLETED,
            isCanceled: status === requestStates.CANCELED
        };
    });
